import { useMemo } from 'react'

import {
  CardTitle,
  ScrollFade,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  TruncatedText,
} from '@/components/ui'
import { amountTone, cn, numberToCurrency } from '@/utils'
import { AccountTypeBadge } from '../AccountTypeBadge'
import { SortableHead, useSort } from '../tables/sort'
import type { PortfolioAccountSummary } from './types'
import { ValidationBadge } from './ValidationBadge'

const headers = [
  ['Account', 'accountNumber', 'w-[170px]'],
  ['Type', 'accountType', 'w-[90px]'],
  ['Market value', 'marketValue', 'text-right w-[150px]'],
  ['Unrealized', 'unrealized', 'text-right w-[170px]'],
  ['Statements', 'periodCount', 'text-right w-[110px]'],
  ['Validation', 'validated', 'w-[190px]'],
]

interface AccountsRollupProps {
  accounts: PortfolioAccountSummary[]
}

// Sortable table of every securities account. Market value and unrealized are each account's
// latest statement snapshot.
export const AccountsRollup = ({ accounts }: AccountsRollupProps) => {
  const { sort, setSort, sorter } = useSort({
    key: 'marketValue',
    direction: 'desc',
  })

  const sortedAccounts = useMemo(
    () => [...accounts].sort(sorter),
    [accounts, sort],
  )

  return (
    <section className="overflow-hidden rounded-lg border bg-white text-blue-7">
      <CardTitle className="px-6 py-4">Accounts</CardTitle>
      <ScrollFade
        containerClassName="w-full border-t"
        className="max-h-[400px]"
      >
        {/* min-w floor = sum of the column widths below. Update when columns change. */}
        <table className="w-full min-w-[880px] caption-bottom text-sm table-fixed">
          <TableHeader className="sticky top-0 z-10 bg-blue-1">
            <TableRow className="bg-blue-1">
              {headers.map(([label, sortKey, className]) => (
                <SortableHead
                  key={sortKey}
                  label={label}
                  sortKey={sortKey as string}
                  sort={sort}
                  onClick={setSort}
                  className={cn(className)}
                />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedAccounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium tabular-nums text-blue-6">
                      {account.accountNumber}
                    </span>
                    <TruncatedText
                      text={account.institution}
                      className="max-w-[160px] text-xs text-muted-foreground"
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <AccountTypeBadge type={account.accountType} size="xs" />
                </TableCell>
                <TableCell className="text-right tabular-nums text-blue-6 whitespace-nowrap">
                  {numberToCurrency(account.marketValue)}
                </TableCell>
                <TableCell
                  className={cn(
                    'text-right tabular-nums whitespace-nowrap',
                    account.unrealized == null
                      ? 'text-muted-foreground'
                      : amountTone(account.unrealized),
                  )}
                >
                  {account.unrealized == null ? (
                    '—'
                  ) : (
                    <>
                      {numberToCurrency(account.unrealized)}
                      {account.unrealizedPercent != null && (
                        <span className="ml-1 text-xs opacity-80">
                          ({account.unrealized >= 0 ? '+' : ''}
                          {account.unrealizedPercent.toFixed(1)}%)
                        </span>
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {account.periodCount}
                </TableCell>
                <TableCell>
                  <ValidationBadge
                    validated={account.validated}
                    failingSections={account.failingSections}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </table>
      </ScrollFade>
    </section>
  )
}
